import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Header.scss';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeExternalLink, setActiveExternalLink] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenSubmenu(null);
    setActiveExternalLink(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openSubmenu && !event.target.closest('.nav-item')) {
        setOpenSubmenu(null);
      }
    };

    if (openSubmenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openSubmenu]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleSubmenu = (menu) => {
    if (isMobile) {
      setOpenSubmenu(openSubmenu === menu ? null : menu);
    }
  };

  const handleCloseSubmenu = (external = false, path = '') => {
    setOpenSubmenu(null);
    setIsMobileMenuOpen(false);
    
    if (external && path) {
      setActiveExternalLink(path);
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      // 내부 링크 클릭 시 외부 링크 상태 초기화
      setActiveExternalLink(null);
    }
  };

  // 활성 메뉴 판단 함수
  const isActiveMenu = (path) => {
    // 외부 링크가 활성화되어 있으면 내부 링크는 비활성화
    if (activeExternalLink) return false;
    return location.pathname === path;
  };

  const isActiveExternalMenu = (path) => {
    return activeExternalLink === path;
  };

  const isActiveSubmenu = (submenu) => {
    // 외부 링크가 활성화된 경우, 해당 외부 링크가 이 서브메뉴에 있는지만 확인
    if (activeExternalLink) {
      return submenu.some(item => item.external && activeExternalLink === item.path);
    }
    // 외부 링크가 없으면 내부 링크만 확인
    return submenu.some(item => !item.external && location.pathname === item.path);
  };

  const menuItems = [
    {
      label: 'HOME',
      path: '/',
    },
    {
      label: 'ABOUT 지온나래',
      submenu: [
        { label: '지온나래란?', path: '/about' },
        { label: '지온나래 활동', path: '/about/activities' },
        { label: '홍보대사 프로필', path: '/about/ambassadors' },
      ],
    },
    {
      label: 'GIST 투어',
      submenu: [
        { label: '캠퍼스 투어', path: '/tour' },
        { label: '캠퍼스 투어 후기', path: '/tour/reviews' },
        { label: '캠퍼스 사이버 투어', path: 'https://www.gist.ac.kr/cyber/', external: true },
        { label: '홍보관 및 명예의 전당', path: 'https://www.gist.ac.kr/kr/html/sub01/010306.html', external: true },
        { label: 'GIST 디지털 역사관', path: 'https://gist30.kr/main/', external: true },
        { label: '찾아오시는 길', path: 'https://www.gist.ac.kr/kr/html/sub01/010305.html', external: true },
      ],
    },
    {
      label: '새내기 GISTian 가이드',
      submenu: [
        { label: 'GISTian 생활', path: '/guide/life' },
        { label: 'GISTian 학업', path: '/guide/academics' },
        { label: '선배 GISTian의 조언', path: '/guide/advice' },
      ],
    },
  ];

  return (
    <>
      <section className={`sec-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="Header">
          <div className="box-main">
            <div className="box-logo">
              <Link to="/" className="logo-link">
                <img src="/logo.png" alt="지온나래" className="logo-image" />
                <div className="logo-text">
                  <div className="logo-main">지온나래</div>
                  <div className="logo-sub">Gionnarae</div>
                </div>
              </Link>
            </div>

            <nav className={`box-nav ${isMobileMenuOpen ? 'open' : ''}`}>
              {menuItems.map((item, index) => (
                <div 
                  key={index} 
                  className="nav-item" 
                  {...(!isMobile && {
                    onMouseEnter: () => setOpenSubmenu(item.label),
                    onMouseLeave: () => setOpenSubmenu(null)
                  })}
                >
                  {item.path ? (
                    <Link to={item.path} className={`nav-link ${isActiveMenu(item.path) ? 'active' : ''}`}>
                      {item.label}
                    </Link>
                  ) : (
                    <>
                      <button
                        className={`nav-link ${isActiveSubmenu(item.submenu) ? 'active' : ''}`}
                        onClick={() => toggleSubmenu(item.label)}
                      >
                        {item.label}
                      </button>
                      {item.submenu && (
                        <div className={`box-submenu ${openSubmenu === item.label ? 'open' : ''}`}>
                          {item.submenu.map((subItem, subIndex) => (
                            <div key={subIndex} className="submenu-item">
                              {subItem.external ? (
                                <Link
                                  to={subItem.path}
                                  className={`submenu-link ${isActiveExternalMenu(subItem.path) ? 'active' : ''}`}
                                  target="_blank"
                                  onClick={() => handleCloseSubmenu(true, subItem.path)}
                                >
                                  {subItem.label}
                                </Link>
                              ) : (
                                <Link 
                                  to={subItem.path} 
                                  className={`submenu-link ${isActiveMenu(subItem.path) ? 'active' : ''}`}
                                  onClick={() => handleCloseSubmenu()}
                                >
                                  {subItem.label}
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </nav>

            <div
              className={`box-mobile-toggle ${isMobileMenuOpen ? 'open' : ''}`}
              onClick={toggleMobileMenu}
            >
              <span className="bar"></span>
              <span className="bar"></span>
              <span className="bar"></span>
            </div>
          </div>
        </div>
      </section>
      <div className="header-spacer"></div>
    </>
  );
};

export default Header;
